// services/authService.js
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Token expiration time in milliseconds (8 hours = 28800000 ms)
const TOKEN_EXPIRATION_MS = 8 * 60 * 60 * 1000;

// Warning time before expiration (5 minutes = 300000 ms)
const WARNING_BEFORE_EXPIRATION_MS = 5 * 60 * 1000;

/**
 * Get the current auth token from localStorage
 */
export function getAuthToken() {
  try {
    return localStorage.getItem('authToken');
  } catch (e) {
    return null;
  }
}

/**
 * Get the current auth user from localStorage
 */
export function getAuthUser() {
  try {
    const raw = localStorage.getItem('authUser');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Save auth data to localStorage and set expiration timer
 * @param {string} token - JWT token
 * @param {object} user - User object
 */
export function saveAuthData(token, user) {
  localStorage.setItem('authToken', token);
  localStorage.setItem('authUser', JSON.stringify(user));
  
  // Store the time when the token was created
  localStorage.setItem('authTokenCreatedAt', Date.now().toString());
}

/**
 * Clear auth data from localStorage
 */
export function clearAuthData() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  localStorage.removeItem('authTokenCreatedAt');
  localStorage.removeItem('authWarningShown');
}

/**
 * Check if the token is expired
 * @returns {boolean} - True if token is expired
 */
export function isTokenExpired() {
  const tokenCreatedAt = localStorage.getItem('authTokenCreatedAt');
  if (!tokenCreatedAt) return true;
  
  const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
  return elapsed >= TOKEN_EXPIRATION_MS;
}

/**
 * Get remaining time before token expiration in milliseconds
 * @returns {number} - Remaining time in ms, or 0 if expired
 */
export function getTokenRemainingTime() {
  const tokenCreatedAt = localStorage.getItem('authTokenCreatedAt');
  if (!tokenCreatedAt) return 0;
  
  const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
  const remaining = TOKEN_EXPIRATION_MS - elapsed;
  return Math.max(0, remaining);
}

/**
 * Format remaining time to human-readable string
 * @param {number} ms - Time in milliseconds
 * @returns {string} - Formatted string
 */
export function formatRemainingTime(ms) {
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

/**
 * Setup a timer that warns the user before token expiration
 * @param {function} onWarning - Callback when warning should be shown
 * @param {function} onExpired - Callback when token has expired
 * @returns {function} - Cleanup function to remove timers
 */
export function setupTokenExpirationTimers(onWarning, onExpired) {
  const remaining = getTokenRemainingTime();
  
  if (remaining <= 0) {
    // Token already expired
    if (onExpired) onExpired();
    return () => {};
  }
  
  const warningTime = remaining - WARNING_BEFORE_EXPIRATION_MS;
  
  // Set warning timer (5 minutes before expiration)
  let warningTimeout = null;
  let expiredTimeout = null;
  
  if (warningTime > 0) {
    warningTimeout = setTimeout(() => {
      if (onWarning) {
        onWarning(formatRemainingTime(WARNING_BEFORE_EXPIRATION_MS));
      }
    }, warningTime);
  } else {
    // Less than 5 minutes remaining, warn immediately
    if (onWarning) {
      onWarning(formatRemainingTime(remaining));
    }
  }
  
  // Set expiration timer
  expiredTimeout = setTimeout(() => {
    if (onExpired) onExpired();
  }, remaining);
  
  // Return cleanup function
  return () => {
    if (warningTimeout) clearTimeout(warningTimeout);
    if (expiredTimeout) clearTimeout(expiredTimeout);
  };
}

/**
 * Create an axios instance with automatic token handling
 */
export const authAxios = axios.create({
  baseURL: API_BASE,
});

// Add request interceptor to include auth token
authAxios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      console.warn('⚠️ Token expirado o inválido - cerrando sesión');
      clearAuthData();
      
      // Dispatch custom event so components can react
      window.dispatchEvent(new CustomEvent('auth:tokenExpired'));
      
      return Promise.reject({
        ...error,
        message: 'Sesión expirada. Por favor inicia sesión de nuevo.',
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Login function
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<object>} - Login response data
 */
export async function login(email, password) {
  try {
    const res = await axios.post(`${API_BASE}/api/auth/login`, {
      email,
      password,
    });
    
    const { token, user } = res.data;
    
    if (token && user) {
      saveAuthData(token, user);
    }
    
    return res.data;
  } catch (err) {
    console.error('Error en login:', err);
    throw err;
  }
}

/**
 * Logout function
 */
export function logout() {
  clearAuthData();
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getAuthToken();
  return !!token && !isTokenExpired();
}
